import CryptoJS from "crypto-js";
import { atom } from "jotai";
import { atomWithHash } from "jotai-location";
import Router from "next/router";

import type { Option, SetCodeEnum } from "@sorcery/db";

import type { CardSortEnum } from "~/types/card";
import { CARD_SORT_DEFAULT } from "~/types/card";
import type { DeckSortEnum } from "~/types/deck";
import { DECK_SORT_DEFAULT, DeckDividerEnum } from "~/types/deck";
import { SEARCH_SORT } from "~/utils/filters";

export type SearchType = {
  query: string;
  set: SetCodeEnum | "*";
  filters: Option[];
  csort: CardSortEnum | typeof SEARCH_SORT;
  dsort: DeckSortEnum | typeof SEARCH_SORT;
  divider: DeckDividerEnum;
  avatar: string; // id of the avatar | '*' for all
};

// Base64 URL Encoding Utility
const base64UrlEncode = (str: string): string => {
  return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, ""); // Remove padding
};

// Base64 URL Decoding Utility
const base64UrlDecode = (str: string): string => {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  return CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(base64));
};

const reset = () => {
  const { replace, pathname, query } = Router;
  void replace(
    {
      pathname,
      query,
      hash: undefined,
    },
    undefined,
    { shallow: true, scroll: false },
  );
};

const encode = (value: SearchType | undefined): string => {
  if (!value) {
    reset();
    return "";
  }

  try {
    return base64UrlEncode(JSON.stringify(value)); // Use Base64 URL Encoding
  } catch (error) {
    console.error("Error encoding custom search options in url hash:", error);
    // bit awkward here, i believe this is a race condition and the return value clears the hash but
    // leaves the #search= portion, then the router.replace method clears it enturely as its async so finishes after
    reset();
    return "";
  }
};

const decode = (encodedValue: string | undefined): SearchType | undefined => {
  if (!encodedValue) return;
  try {
    const jsonString = base64UrlDecode(encodedValue);
    return JSON.parse(jsonString) as SearchType;
  } catch (error) {
    console.error("Error decoding custom search options in url hash:", error);
    // bit awkward here, i believe this is a race condition and the return value clears the hash but
    // leaves the #search= portion, then the router.replace method clears it enturely as its async so finishes after
    reset();
    return undefined;
  }
};

// underlying
const _searchJotai = atomWithHash<SearchType | undefined>("search", undefined, {
  serialize: encode,
  deserialize: decode,
});

// readonly
export const searchJotai = atom((get) => get(_searchJotai));

// writeonly
export const setSearchJotai = atom(undefined, (get, set, newData: Partial<SearchType>) => {
  // use prev values as baseline, then set any custom properties that are defined
  const prev = {
    ...(get(searchJotai) ?? {
      query: "",
      set: "*",
      filters: [],
      csort: CARD_SORT_DEFAULT,
      dsort: DECK_SORT_DEFAULT,
      divider: DeckDividerEnum.ALL,
      avatar: "*",
    }),
  };
  // overlay new values
  const newV = {
    ...prev,
    query: newData.query ?? prev.query,
    set: newData.set ?? prev.set,
    filters: newData.filters ?? prev.filters,
    csort: newData.query ? SEARCH_SORT : (newData.csort ?? prev.csort),
    dsort: newData.query ? SEARCH_SORT : (newData.dsort ?? prev.dsort),
    divider: newData.divider ?? prev.divider,
    avatar: newData.avatar ?? prev.avatar,
  };
  // detect if this is a rese to default values
  const isReset =
    newV.query === "" &&
    newV.set === "*" &&
    !newV.filters.length &&
    newV.csort === CARD_SORT_DEFAULT &&
    newV.dsort === DECK_SORT_DEFAULT &&
    newV.divider === DeckDividerEnum.ALL &&
    newV.avatar === "*";

  set(_searchJotai, isReset ? undefined : newV);
});
